import { capitalize as lodashCapitalize, escapeRegExp } from 'lodash-es';
import { removeStopwords } from 'stopword';

export const removeAllSpaces = (text: string) => {
  return text.replace(/\s/g, '');
};

export const uncapitalize = (text: string) => {
  return `${text[0].toLowerCase()}${text.slice(1)}`;
};

/**
 * Uppercases the first character of a string and leaves the rest unchanged.
 *
 * Unlike {@link https://lodash.com/docs/#capitalize lodash `capitalize`}, which lowercases
 * every character after the first, this helper preserves existing casing — useful for
 * display labels streamed from backends (acronyms, mixed case, etc.).
 *
 * Leading and trailing whitespace is trimmed before capitalizing.
 *
 * @param text - Input string to capitalize
 * @returns Trimmed string with an uppercase first character, or an empty string when input is blank
 *
 * @example
 * capitalize('generating response') // 'Generating response'
 *
 * @example
 * capitalize('  reading question  ') // 'Reading question'
 *
 * @example
 * // lodash capitalize would return 'Sql query'
 * capitalize('SQL query') // 'SQL query'
 *
 * @example
 * // lodash capitalize would return 'Generating response'
 * capitalize('GENERATING response') // 'GENERATING response'
 */
export const capitalize = (text: string) => {
  const trimmed = text.trim();

  if (!trimmed) return trimmed;

  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

export const replaceSubstring = (originalString: string, start: number, end: number, newSubstring: string) => {
  return originalString.slice(0, start) + newSubstring + originalString.slice(end);
};

// Create a function to filter out objects with similar names
export const filterSimilarNames = (arr: { name: string; value: string }[]) => {
  const uniqueNames = new Set();
  return arr.filter((obj) => {
    if (!uniqueNames.has(obj.name)) {
      uniqueNames.add(obj.name);
      return true;
    }
    return false;
  });
};

export const separateTextByLineBreaks = (text: string) => {
  return text.replace(/\n+/g, '\n').split('\n');
};

export const sortAlphabetically = (textA: string, textB: string) => {
  return textA.localeCompare(textB);
};

export const matchWords = (sourceText: string, textToMatch: string, matchWholeWords = true) => {
  const regex = matchWholeWords
    ? new RegExp(`\\b${escapeRegExp(textToMatch)}\\b`, 'i')
    : new RegExp(escapeRegExp(textToMatch), 'i');

  return regex.test(sourceText);
};

export const removeEnglishStopwords = (texts: string[]) => {
  return removeStopwords(texts);
};

/**
 * Joins names into a readable list, keeping at most `max` of them and counting the rest.
 *
 * @example
 * ```typescript
 * formatCappedList(['a.zip', 'b.zip'])                    // "a.zip, b.zip"
 * formatCappedList(['a.zip', 'b.zip', 'c.zip', 'd.zip'])  // "a.zip, b.zip, c.zip and 1 more"
 * ```
 */
export const formatCappedList = (names: string[], max = 3) => {
  const listedNames = names.slice(0, max).join(', ');
  const remainingCount = names.length - max;

  return remainingCount > 0 ? `${listedNames} and ${remainingCount} more` : listedNames;
};

export const singularOrPlural = (singular: string, plural: string) => (count?: number | null) => {
  if (typeof count !== 'number') return singular;

  if (Math.abs(count) === 1) return singular;

  return plural;
};

export const snakeCaseToDisplayText = (text: string) => {
  return text.split('_').map(lodashCapitalize).join(' ');
};

const SNAKE_CASE_TOKEN_PATTERN = /\b[a-z][a-z0-9]*(?:_[a-z0-9]+)+\b/g;

/**
 * Converts a delimited machine identifier into title-case display text.
 *
 * Splits on underscores and hyphens, normalizes each segment to lowercase,
 * then capitalizes the first letter of every word.
 *
 * @param identifier - A snake_case or kebab-case token (e.g. `read_condition_summary`)
 * @returns Title-case words joined by spaces (e.g. `Read Condition Summary`)
 *
 * @example
 * humanizeSnakeCaseIdentifier('read_condition_summary') // 'Read Condition Summary'
 * humanizeSnakeCaseIdentifier('search-client-files') // 'Search Client Files'
 */
export const humanizeSnakeCaseIdentifier = (identifier: string) =>
  identifier
    .split(/[_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

/**
 * Humanizes lowercase snake_case tokens embedded in a larger string.
 *
 * Only matches tokens that start with a lowercase letter and contain at
 * least one underscore (e.g. `read_condition_summary`). Surrounding prose,
 * punctuation, and non-matching tokens are left unchanged.
 *
 * @param text - Free-form text that may contain snake_case identifiers
 * @returns The input with each matching token replaced by {@link humanizeSnakeCaseIdentifier}
 *
 * @example
 * humanizeSnakeCaseTokensInText('Used read_condition_summary') // 'Used Read Condition Summary'
 * humanizeSnakeCaseTokensInText('Searched: "hearing transcript"') // unchanged
 */
export const humanizeSnakeCaseTokensInText = (text: string) =>
  text.replace(SNAKE_CASE_TOKEN_PATTERN, humanizeSnakeCaseIdentifier);

export const getDomainFromEmail = (email: string) => {
  return email.split('@')[1];
};

export const pascalCaseWithSpaces = (text: string) => {
  return text.replace(/([A-Z])/g, ' $1').trim();
};

export const extractPercentNumber = (text: string) => {
  return text.match(/(\d+(\.\d+)?)%/)?.[1];
};

export const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

export const removeLineBreaks = (text: string, replacer = '') => {
  return text.replace(/\n/g, replacer);
};

export const removeSpecialChars = (text: string, replacer = '') => {
  return text.replace(/[^A-Z0-9]+/gi, replacer);
};

export const isEmail = (text: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const hasConsecutiveDots = /\.{2,}/.test(text);

  return emailRegex.test(text) && !hasConsecutiveDots;
};

export const hasLowercaseLetter = (text: string) => {
  return /[a-z]/.test(text);
};

export const hasUppercaseLetter = (text: string) => {
  return /[A-Z]/.test(text);
};

export const hasSpecialChar = (text: string) => {
  return /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(text);
};

export const isNumber = (text: string) => {
  return /^\d*$/.test(text);
};

export const lowercaseSearch = (text: string, search: string) => {
  return text.toLowerCase().includes(search.toLowerCase());
};
